import React, { useState, useEffect } from 'react'
import {
  loadStripe,
  Stripe,
  StripeEmbeddedCheckout,
} from '@stripe/stripe-js'
import Button from '@/elements/Button/Button.component'

interface StripeCheckoutProps {
  publicApiKey: string
  generateOrderApiUrl: string
  selectedAmount: number | null
  isMonthly: boolean
  formId: number
  donateButtonLabel: string
  currencyCode?: string
}

const StripeCheckout = (
  {
    publicApiKey,
    generateOrderApiUrl,
    selectedAmount,
    isMonthly,
    formId,
    currencyCode,
    donateButtonLabel,
  }: StripeCheckoutProps
) => {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [paymentMessage, setPaymentMessage] = useState<string>('')
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false)
  const [checkoutInstance, setCheckoutInstance] = useState<StripeEmbeddedCheckout | null>(null);

  const [requestVerificationToken, setRequestVerificationToken] = useState<
    string | null
  >(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false)

  useEffect(() => {
    const initializeStripe = async () => {
      const stripeInstance = await loadStripe(publicApiKey)
      setStripe(stripeInstance)
    }

    initializeStripe()

    const tokenInput = document.getElementsByName(
      '__RequestVerificationToken'
    )[0] as HTMLInputElement
    if (tokenInput) {
      setRequestVerificationToken(tokenInput.value)
    }
  }, [publicApiKey])

  const handleContinue = async () => {
    if (!stripe) return

    setLoading(true)

    try {
      const response = await fetch(generateOrderApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Requestverificationtoken: requestVerificationToken || '',
        },
        body: JSON.stringify({
          amount: selectedAmount,
          currency: currencyCode,
          formId: formId,
          isRecurring: isMonthly,
          currentUrl: window.location.href,
        }),
      })

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status} - ${response.statusText}`
        )
      }

      const data = await response.json();
      setShowPaymentModal(true);

      if (stripe) {
        const newCheckout = await stripe.initEmbeddedCheckout({
          clientSecret: data.clientSecret,
        });
        setCheckoutInstance(newCheckout);
        setShowPaymentForm(true);
      }

    } catch (error) {
      console.error('Error fetching client secret:', error)
      setPaymentMessage('Error: Client Secret Error.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setShowPaymentModal(false);
    setLoading(false);
    setShowPaymentForm(false);
    if (checkoutInstance) {
      // @ts-ignore
      checkoutInstance.embeddedCheckout.destroy();
      setCheckoutInstance(null);
    }
  };

  useEffect(() => {
    if (showPaymentModal && showPaymentForm && checkoutInstance) {
      // Mount the checkout once the element is available in the DOM
      checkoutInstance.mount('#payment-element');
    }
  }, [showPaymentModal, showPaymentForm, checkoutInstance]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showPaymentModal) {
        handleClose();
      }
    };

    // Attach the event listener when the component mounts
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPaymentModal]);

  return (
    <div>
      <div className='form-group hidden'>
        <input
          readOnly
          type='number'
          name='donationBlockId'
          id='donationBlockId'
          placeholder='378'
          value={formId ?? ''}
          required
        />
      </div>

      <div className='form-group hidden'>
        <input
          readOnly
          type='number'
          name='donationAmount'
          id='donationAmount'
          placeholder='$20'
          value={selectedAmount ?? ''}
          required
        />
      </div>

      <div className='form-group hidden'>
        <input
          readOnly
          type='checkbox'
          name='isRecurring'
          id='isRecurring'
          checked={isMonthly}
        />
      </div>


      <Button
        as='button'
        className='w-full'
        size='xl'
        id='btnContinue'
        type='button'
        onClick={handleContinue}
        disabled={!selectedAmount}>
        {loading ? 'Loading...' : donateButtonLabel}
      </Button>

      {showPaymentModal && (
        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0'>
            <div
              className='fixed inset-0 transition-opacity'
              aria-hidden='true'>
              <div className='absolute inset-0 bg-gray-500 opacity-75'></div>
            </div>

            <span
              className='hidden sm:inline-block sm:h-screen sm:align-middle'
              aria-hidden='true'>
              &#8203;
            </span>

            <div className='inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all md:min-w-[80%]'>
              <div className='relative bg-white px-4 !pt-4 pb-4 sm:p-6 sm:pb-4'>
                <div className='flex w-full flex-row-reverse pb-2'>
                  <Button
                    size='xs'
                    icon='xmark'
                    iconPosition='after'
                    className='flex'
                    onClick={handleClose}
                  />
                </div>
                <div id='payment-element' className='mb-4'></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div id='payment-message text-xs text-red-500 p-2'>{paymentMessage}</div>
    </div>
  )
}

export default StripeCheckout
